/**
 * Single source of truth for the Labs metadata registry and sequential/related
 * lab discovery.
 *
 * Kept free of React and lazy-chunk imports so lightweight callers (header
 * search, route title resolution, corpus generation, and tests) can query all
 * 29 labs without pulling demo bundles into the main chunk.
 */

export interface LabMeta {
    id: string;
    title: string;
    desc: string;
    category: 'Visualisers' | 'Performance' | 'Inference' | 'Training' | 'RAG' | 'Tools' | 'Courses' | 'DevOps';
    /** Concise keyword-rich summary used by lexical search indexing. */
    searchSummary: string;
    fullscreen?: boolean;
}

export const LAB_CATALOG: readonly LabMeta[] = [
    {
        id: 'kiln-agentic-3d',
        title: 'An Agent Writes 3D',
        desc: 'Three original 3D models built from code: Signal Loom, Stack Descent and a terminal. Orbit any piece, inspect its construction, and download the GLB.',
        category: 'Visualisers',
        searchSummary: 'Signal Loom, Stack Descent and terminal: original procedural geometry, downloadable GLB',
    },
    {
        id: 'pretext-layout',
        title: 'Zero-Reflow Text Layout',
        desc: 'DOM measurement triggers layout reflow. Pretext uses OffscreenCanvas instead — pure arithmetic, no DOM touch. Race them: 1000 measurements each, live.',
        category: 'Performance',
        searchSummary: 'Pretext OffscreenCanvas DOM measurement benchmark',
    },
    {
        id: 'laya-decisions',
        title: 'Decide, Don\u2019t Write',
        desc: 'Give it a state and typed questions; get typed answers with calibrated probabilities and no prose. A working System One playground, plus why the probability can be trusted.',
        category: 'Inference',
        searchSummary: 'Laya typed answers, calibrated probabilities',
    },
    {
        id: 'the-gate',
        title: 'The Gate',
        desc: 'Two cheap checks run in this tab before anything is sent anywhere: what language this is, and whether the site already answers it. Only what survives the gate costs a token.',
        category: 'Inference',
        searchSummary: 'on-device language and corpus triage',
    },
    {
        id: 'tongue-on-device',
        title: 'Language ID on Device',
        desc: 'Two megabytes of int8 weights, a character n-gram hash and one 59x32 matmul. No inference runtime and no WASM — and the text you type is never sent anywhere.',
        category: 'Inference',
        searchSummary: '84 languages, 2MB int8 weights, no runtime',
    },
    {
        id: 'backprop-forge',
        title: 'Backprop Forge',
        desc: '106k-parameter transformer training live in your browser tab. Hand-derived backward pass, SIMD WebAssembly kernel.',
        category: 'Training',
        searchSummary: 'transformer training, backward pass, SIMD WebAssembly',
        fullscreen: true,
    },
    {
        id: 'attention-explorer',
        title: 'Attention Head Explorer',
        desc: 'DistilBERT running in your browser. All 6 layers × 12 heads as interactive heatmaps.',
        category: 'Inference',
        searchSummary: 'DistilBERT attention heatmaps',
    },
    {
        id: 'math-history',
        title: 'From Counting to Maxwell',
        desc: 'Intuition-first math history — √2 crisis to Maxwell\'s equations. Interactive proofs, live canvas, 26-stop timeline.',
        category: 'Visualisers',
        searchSummary: 'math history, interactive proofs, timeline',
    },
    {
        id: 'onnx-model-surgery',
        title: 'ONNX Model Surgery',
        desc: 'Computation graph explorer — DistilBERT, MobileNetV2, ResNet-50, GPT-2 as zoomable SVG DAGs.',
        category: 'Inference',
        searchSummary: 'computation graph explorer, SVG DAGs',
    },
    {
        id: 'loss-landscape',
        title: 'Loss Landscape',
        desc: '20.5 million forward passes computed live by a WASM kernel. Animated gradient-descent path.',
        category: 'Training',
        searchSummary: 'optimiser gradient descent paths',
    },
    {
        id: 'lora-explorer',
        title: 'LoRA Rank Explorer',
        desc: 'Watch matrix decomposition in real time. Adjust rank r=1–8, see W, BA approximation, error — all live heatmaps.',
        category: 'Training',
        searchSummary: 'low-rank adaptation, parameter count',
    },
    {
        id: 'reranker',
        title: 'Bi-encoder vs Cross-encoder',
        desc: 'Live reranking comparison. See rank deltas as you type — why cross-encoders beat bi-encoders at precision.',
        category: 'Inference',
        searchSummary: 'reranking and rank deltas',
    },
    {
        id: 'quantisation',
        title: 'Quantisation Explorer',
        desc: 'FP32 → INT4 → GPTQ-4bit. Live weight distribution histograms, model size deltas, reconstruction error.',
        category: 'Inference',
        searchSummary: 'FP32 INT4 GPTQ precision and model size',
    },
    {
        id: 'inference-race',
        title: 'JS vs WASM vs ONNX',
        desc: 'Head-to-head inference benchmark. Three runtimes, three tasks, animated live race.',
        category: 'Inference',
        searchSummary: 'inference runtime throughput and latency',
    },
    {
        id: 'rag-pipeline',
        title: 'Live RAG Pipeline',
        desc: 'Type a query — MiniLM embeds it in your browser, runs cosine similarity, returns ranked results.',
        category: 'RAG',
        searchSummary: 'embed, retrieve, rank, return',
    },
    {
        id: 'pagerank-graph',
        title: 'Interactive PageRank',
        desc: 'Add nodes and edges, watch scores redistribute in real time. Adjust the damping factor.',
        category: 'RAG',
        searchSummary: 'power iteration, link graph, damping factor',
    },
    {
        id: 'neural-net-wgpu',
        title: 'Manim Animations',
        desc: 'Backprop, transformer attention, PageRank — rendered with Manim Community Edition, proper LaTeX.',
        category: 'Visualisers',
        searchSummary: 'backprop, attention, PageRank, LaTeX',
    },
    {
        id: 'motion-canvas',
        title: 'Motion Canvas Scenes',
        desc: 'TypeScript animation scenes rendered live in your browser. Same engine 3Blue1Brown uses.',
        category: 'Visualisers',
        searchSummary: 'TypeScript animation scenes',
    },
    {
        id: 'vector-calculus',
        title: 'Vector Calculus',
        desc: 'Dot product, cross product, gradient, divergence, curl — all interactive with live canvas.',
        category: 'Visualisers',
        searchSummary: 'dot cross product, gradient, divergence, curl',
    },
    {
        id: 'image-classifier',
        title: 'Image Classifier',
        desc: 'MobileNet v2 via TensorFlow.js + WebGL. Drop any image, get top-3 predictions in under 200 ms.',
        category: 'Inference',
        searchSummary: 'MobileNet TensorFlow.js WebGL predictions',
    },
    {
        id: 'extractive-qa',
        title: 'Extractive Q&A',
        desc: 'DistilBERT-SQuAD runs locally. Paste text, ask a question, get the exact answer span.',
        category: 'Inference',
        searchSummary: 'DistilBERT SQuAD question answering',
    },
    {
        id: 'semantic-search',
        title: 'Semantic Search',
        desc: 'all-MiniLM-L6-v2 in WebAssembly. Search by meaning over an AI/ML document corpus.',
        category: 'RAG',
        searchSummary: 'MiniLM embeddings and cosine similarity',
    },
    {
        id: 'on-device-calc',
        title: 'On-Device AI Calculator',
        desc: 'Pick model + quantisation + device — see weight size, peak RAM, tokens/sec, recommended format.',
        category: 'Tools',
        searchSummary: 'model quantisation device RAM tokens per second',
    },
    {
        id: 'format-tree',
        title: 'ML Format Decision Tree',
        desc: 'Filter by deployment target and see the relevant formats, runtimes, and hardware.',
        category: 'Tools',
        searchSummary: 'deployment targets, formats, runtimes, hardware',
    },
    {
        id: 'ai-ecosystem',
        title: 'AI Stack Selector',
        desc: 'Pick a deployment target — get the exact format + engine + hello-world code.',
        category: 'Tools',
        searchSummary: 'deployment format engine hello-world code',
    },
    {
        id: 'data-engineering',
        title: 'Data Engineering',
        desc: '8-module self-paced course: Advanced SQL, dbt, Spark, Airflow, Kafka, Delta Lake, full capstone.',
        category: 'Courses',
        searchSummary: 'SQL dbt Spark Airflow Kafka Delta Lake',
        fullscreen: true,
    },
    {
        id: 'math-for-ml',
        title: 'Math for ML',
        desc: '15-panel course — linear algebra, calculus, vector calculus, statistics — every concept in production context.',
        category: 'Courses',
        searchSummary: 'linear algebra, calculus, statistics',
        fullscreen: true,
    },
    {
        id: 'docker-simulator',
        title: 'Docker Simulator',
        desc: 'Build images, run containers, compose multi-service apps — terminal output with live visual state.',
        category: 'DevOps',
        searchSummary: 'build images, run containers, compose services',
    },
    {
        id: 'inverse-kinematics',
        title: 'Reach & constraints',
        desc: 'Move a target and let Fullik solve a three-link arm. Change joint limits, inspect the real residual, and discover why being inside the reach circle is not enough.',
        category: 'Visualisers',
        searchSummary: 'Fullik FABRIK inverse kinematics, three-link arm, joint limits, residual',
    },
    {
        id: 'openui-streaming',
        title: 'Streaming Generative UI',
        desc: 'Stream OpenUI Lang 1.0 token-by-token. Watch auto-closed syntax, top-down statement hoisting, and O(1) incremental patches render live UI with 56% fewer tokens than JSON.',
        category: 'Inference',
        searchSummary: 'OpenUI Lang streaming generative UI parser, statement hoisting, incremental patching, token efficiency',
    },
    {
        id: 'match-cut-lab',
        title: 'Match-Cut Relay & QC',
        desc: '150 BPM match-cut montage mathematics: Template-A constant-cover camera roll, anchor-matched affine geo warps, whip-cut profiles, and 50/50 onion-skin cut alignment.',
        category: 'Visualisers',
        searchSummary: 'Sac-Y match-cut montage, Template-A cover zoom, affine geo alignment, whip pan, onion skin QC',
    },
] as const;

export const LAB_CATEGORIES = ['All', ...Array.from(new Set(LAB_CATALOG.map(l => l.category)))] as const;

export function findLabMeta(id?: string | null): LabMeta | null {
    if (!id) return null;
    return LAB_CATALOG.find(lab => lab.id === id) ?? null;
}

export function getAdjacentLabs<T extends { id: string }>(
    labs: readonly T[],
    currentId: string,
): { prev: T | null; next: T | null; index: number; total: number } {
    const index = labs.findIndex(lab => lab.id === currentId);
    if (index === -1) return { prev: null, next: null, index: -1, total: labs.length };
    return {
        prev: index > 0 ? labs[index - 1] : null,
        next: index < labs.length - 1 ? labs[index + 1] : null,
        index,
        total: labs.length,
    };
}
